import type { FormikProps } from 'formik';
import { Text } from '@rsos/base-components';
import { ErrorWrapper } from '@rsos/base-components/form/ErrorsWrapper';
import { Select } from '@rsos/components/capstone/base';
import { Label } from '../../../../../AirAmbulanceForm/AirAmbulanceForm/AirAmbulanceFormStandalone.styles';
import type {
  TowProFormValues,
  TowProSelectOption,
} from '../towProFormConstants';
import styles from './FormField.module.css';
import type { TowProStringListField } from './fieldTypes';
import { useSelectStyles } from './useSelectStyles';

export interface MultiSelectFieldProps {
  formik: FormikProps<TowProFormValues>;
  name: TowProStringListField;
  label: string;
  options: TowProSelectOption[];
  placeholder: string;
  isDisabled?: boolean;
}

function MultiSelectField({
  formik,
  name,
  label,
  options,
  placeholder,
  isDisabled = false,
}: MultiSelectFieldProps): JSX.Element {
  const selected = formik.values[name];
  const selectedOptions = options.filter(option =>
    selected.includes(option.value),
  );
  const error =
    formik.touched[name] && formik.errors[name]
      ? String(formik.errors[name])
      : undefined;

  const selectStyles = useSelectStyles(error);

  return (
    <div className={styles.field}>
      <Label>
        <Text size="small" color="primaryText">
          {label}
        </Text>
      </Label>
      <Select
        isMulti
        name={name}
        classNamePrefix={name}
        options={options}
        placeholder={placeholder}
        isClearable={false}
        isSearchable={false}
        isDisabled={isDisabled}
        onChange={(selection: readonly TowProSelectOption[] | null) => {
          formik.setFieldValue(name, selection?.map(o => o.value) ?? [], true);
          formik.setFieldTouched(name, true, false);
        }}
        // Touch only; see SelectField for why blur must not re-validate.
        onBlur={() => formik.setFieldTouched(name, true, false)}
        value={selectedOptions}
        styles={selectStyles}
      />
      {error && <ErrorWrapper error={error} />}
    </div>
  );
}

export default MultiSelectField;
